import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import request from "../../../utils/request";
import { classNames } from "./utils";
import { SafeHtml, htmlToPreview } from "../../../utils/sanitizeHtml";

// Lightweight CRUD UI over the existing admin /templates endpoint. Lives
// next to the reply modal so agents can manage canned responses without
// leaving the inquiry workspace. Backend routes are already in place; this
// component only adds the operator surface.
export default function TemplateManagerModal({ open, onClose, onPicked }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState({ name: "", subject: "", html: "" });
  const [saving, setSaving] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatesExpanded, setTemplatesExpanded] = useState(true);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await request.get("/templates", { withCredentials: true });
      const list = data.templates || [];
      setTemplates(list);
      setActiveId((current) =>
        current && list.some((template) => template._id === current)
          ? current
          : list[0]?._id || null
      );
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const active = useMemo(
    () => templates.find((t) => t._id === activeId) || null,
    [templates, activeId]
  );

  const visibleTemplates = useMemo(() => {
    const query = templateSearch.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((template) => {
      const haystack = [
        template?.name,
        template?.subject,
        htmlToPreview(template?.html || "", { maxLength: 500 }),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [templates, templateSearch]);

  useEffect(() => {
    if (active) {
      setDraft({ name: active.name || "", subject: active.subject || "", html: active.html || "" });
    } else {
      setDraft({ name: "", subject: "", html: "" });
    }
  }, [active?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => {
    setActiveId(null);
    setDraft({ name: "", subject: "", html: "" });
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: draft.name.trim(),
        subject: draft.subject,
        html: draft.html,
      };
      if (active?._id) {
        const { data } = await request.patch(`/templates/${active._id}`, payload, {
          withCredentials: true,
        });
        const saved = data.template || { ...active, ...payload };
        setTemplates((list) => list.map((t) => (t._id === saved._id ? saved : t)));
        setActiveId(saved._id);
      } else {
        const { data } = await request.post("/templates", payload, { withCredentials: true });
        const saved = data.template || data;
        if (!saved?._id) throw new Error("Template saved, but the server did not return it");
        setTemplates((list) => [saved, ...list]);
        setActiveId(saved._id);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this template?")) return;
    try {
      await request.delete(`/templates/${id}`, { withCredentials: true });
      setTemplates((list) => list.filter((t) => t._id !== id));
      if (activeId === id) setActiveId(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to delete template");
    }
  };

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => onClose?.()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="relative flex w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
                  <Dialog.Title className="text-base font-semibold text-zinc-900">
                    Response templates
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={() => onClose?.()}
                    className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </header>

                <div className="grid min-h-0 grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)]">
                  <aside className="border-b border-zinc-200 bg-zinc-50/60 md:border-b-0 md:border-r">
                    <div className="flex items-center justify-between gap-2 p-2">
                      <button
                        type="button"
                        onClick={() => setTemplatesExpanded((value) => !value)}
                        className="inline-flex min-w-0 items-center gap-1 rounded-md px-1 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 hover:bg-white hover:text-zinc-700"
                        title={templatesExpanded ? "Collapse saved templates" : "Expand saved templates"}
                      >
                        {templatesExpanded ? (
                          <ChevronDownIcon className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRightIcon className="h-3.5 w-3.5" />
                        )}
                        Templates
                      </button>
                      <button
                        type="button"
                        onClick={startNew}
                        className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
                      >
                        <PlusIcon className="h-3.5 w-3.5" /> New
                      </button>
                    </div>

                    <div className="px-2 pb-2">
                      <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                        <input
                          type="text"
                          value={templateSearch}
                          onChange={(event) => setTemplateSearch(event.target.value)}
                          placeholder="Search saved templates"
                          className="w-full rounded-md border border-zinc-200 bg-white py-1.5 pl-7 pr-2 text-xs text-zinc-700 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        />
                      </div>
                    </div>

                    {!templatesExpanded ? (
                      <div className="px-2 pb-3">
                        <select
                          value={activeId || ""}
                          onChange={(event) => setActiveId(event.target.value || null)}
                          className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-xs text-zinc-700 focus:border-indigo-500 focus:outline-none"
                        >
                          <option value="">New template</option>
                          {visibleTemplates.map((template) => (
                            <option key={template._id} value={template._id}>
                              {template.name || "(unnamed)"}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="max-h-[68vh] overflow-y-auto">
                        {loading && !templates.length ? (
                          <div className="p-3 text-xs text-zinc-500">Loading…</div>
                        ) : null}
                        {visibleTemplates.map((t) => (
                          <div
                            key={t._id}
                            className={classNames(
                              "group flex cursor-pointer items-center justify-between gap-2 border-b border-zinc-100 px-3 py-2 text-sm",
                              activeId === t._id ? "bg-white" : "hover:bg-white"
                            )}
                            onClick={() => setActiveId(t._id)}
                          >
                            <div className="min-w-0">
                              <p className="truncate font-medium text-zinc-900">{t.name}</p>
                              <p className="truncate text-[11px] text-zinc-500">
                                {t.subject || "(no subject)"}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
                              {onPicked ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onPicked(t);
                                  }}
                                  className="rounded-md border border-zinc-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-zinc-700 hover:bg-zinc-50"
                                >
                                  Use
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remove(t._id);
                                }}
                                className="rounded-md p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"
                                title="Delete"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {!loading && templates.length && !visibleTemplates.length ? (
                          <div className="p-3 text-xs text-zinc-500">
                            No templates match that search.
                          </div>
                        ) : null}
                        {!loading && !templates.length ? (
                          <div className="p-3 text-xs text-zinc-500">
                            No templates yet. Create one to speed up common replies.
                          </div>
                        ) : null}
                      </div>
                    )}
                  </aside>

                  <section className="flex min-h-0 flex-col">
                    <div className="grid min-h-0 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.85fr)]">
                      <div className="space-y-3">
                        <div>
                          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            Name
                          </label>
                          <input
                            type="text"
                            value={draft.name}
                            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                            placeholder="e.g. Guest — delivery timing confirmation"
                            className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            Subject
                          </label>
                          <input
                            type="text"
                            value={draft.subject}
                            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                            placeholder="Use {{first_name|there}} for merge variables"
                            className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            Body (HTML or plain text)
                          </label>
                          <textarea
                            rows={18}
                            value={draft.html}
                            onChange={(e) => setDraft((d) => ({ ...d, html: e.target.value }))}
                            placeholder="Hi {{first_name|there}},&#10;&#10;Thanks for reaching out…"
                            className="mt-1 min-h-[420px] w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 font-mono text-[13px] focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                          />
                        </div>
                      </div>

                      <aside className="flex min-h-[420px] flex-col rounded-xl border border-zinc-200 bg-zinc-50/80">
                        <div className="border-b border-zinc-200 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            Preview
                          </p>
                          <p className="mt-1 truncate text-sm font-semibold text-zinc-900">
                            {draft.subject || "No subject"}
                          </p>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto bg-white p-4">
                          {draft.html?.trim() ? (
                            <SafeHtml
                              value={draft.html}
                              className="max-w-none text-sm leading-6 text-zinc-800 [&_a]:text-indigo-600 [&_a]:underline [&_li]:ml-5 [&_li]:list-disc [&_p]:mb-3"
                            />
                          ) : (
                            <p className="text-sm text-zinc-400">
                              Start typing a template body to preview it here.
                            </p>
                          )}
                        </div>
                      </aside>

                      {error ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700 lg:col-span-2">
                          {error}
                        </div>
                      ) : null}

                      <div className="flex items-center justify-end gap-2 lg:col-span-2">
                        <button
                          type="button"
                          onClick={() => onClose?.()}
                          className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                        >
                          Done
                        </button>
                        <button
                          type="button"
                          onClick={save}
                          disabled={saving}
                          className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:bg-zinc-300"
                        >
                          {saving ? "Saving…" : active ? "Save changes" : "Create template"}
                        </button>
                      </div>
                    </div>
                  </section>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
